export const EMPLOYEE_CATEGORY_TABS = [
  { id: 'facility-team', label: 'Facility team' },
  { id: 'emp-and-other', label: 'MEP and other' },
  { id: 'house-keeping', label: 'House keeping' },
  { id: 'security', label: 'Security' },
];

export const DEFAULT_EMPLOYEE_CATEGORY_TAB = 'facility-team';

/** UI tab id → GET /community/schedule/users personnel_type */
export const PERSONNEL_TYPE_BY_CATEGORY_TAB = {
  'facility-team': 'supervisors',
  'emp-and-other': 'employees',
  'house-keeping': 'housekeeping',
  security: 'security',
};

export const EMPLOYEE_CATEGORY_SECTION_LABELS = {
  'facility-team': 'Cluster manager',
  'emp-and-other': 'Employees',
  'house-keeping': 'House keeping',
  security: 'Security',
};

export const createEmptySelectedEmployeesByCategory = () =>
  EMPLOYEE_CATEGORY_TABS.reduce((acc, tab) => {
    acc[tab.id] = [];
    return acc;
  }, {});

export const createEmptyEmployeeDescriptionsByCategory = () =>
  EMPLOYEE_CATEGORY_TABS.reduce((acc, tab) => {
    acc[tab.id] = '';
    return acc;
  }, {});

export const getEmployeeDescriptionByCategory = (selection, categoryId) => {
  const byCategory = selection?.employeeDescriptionsByCategory;
  if (byCategory && typeof byCategory[categoryId] === 'string') {
    return byCategory[categoryId];
  }
  return '';
};

const roleText = employee =>
  (employee?.spec_role || employee?.role_name || employee?.role || '').toLowerCase();

const matchesPersonnelType = (employee, apiType) =>
  apiType && (employee?.personnel_type === apiType || employee?.personnelType === apiType);

/** Filter employees for the active category tab (API personnel_type or role hints). */
export const filterEmployeesByCategory = (employees, categoryId) => {
  if (!Array.isArray(employees)) return [];

  const apiType = PERSONNEL_TYPE_BY_CATEGORY_TAB[categoryId];
  const byApiType = apiType ? employees.filter(e => matchesPersonnelType(e, apiType)) : [];
  if (byApiType.length > 0) return byApiType;

  switch (categoryId) {
    case 'facility-team':
      return employees.filter(
        e =>
          e._source === 'supervisor' ||
          roleText(e).includes('supervisor') ||
          roleText(e).includes('cluster') ||
          roleText(e).includes('manager')
      );
    case 'security':
      return employees.filter(
        e =>
          (e._source === 'guard' || !e._source) &&
          (roleText(e).includes('security') || roleText(e).includes('guard'))
      );
    case 'house-keeping':
      return employees.filter(
        e =>
          (e._source === 'guard' || !e._source) &&
          (roleText(e).includes('house') ||
            roleText(e).includes('keeping') ||
            roleText(e).includes('clean'))
      );
    case 'emp-and-other':
    default:
      return employees.filter(e => {
        if (e._source === 'supervisor') return false;
        const role = roleText(e);
        return (
          !role.includes('security') &&
          !role.includes('house') &&
          !role.includes('keeping') &&
          !role.includes('clean')
        );
      });
  }
};

export const getSelectedEmployeeIds = (selection, categoryId) => {
  const byCategory = selection?.selectedEmployeeIdsByCategory;
  if (byCategory && Array.isArray(byCategory[categoryId])) {
    return byCategory[categoryId];
  }
  return [];
};

export const createEmptyFloorsState = (floors = [], tasks = []) =>
  floors.reduce((acc, floor) => {
    acc[floor.id] = {
      selected: false,
      tasks: tasks.reduce((taskAcc, task) => {
        taskAcc[task.id] = false;
        return taskAcc;
      }, {}),
      description: '',
    };
    return acc;
  }, {});

/** Security tab: simple floor checkbox (no task collapsibles). */
export const isFloorChecked = floorState => !!floorState?.selected;

export const areAllFloorsCheckedForBlock = (selection, blockId, floors = []) => {
  const blockFloors = getBlockFloors(selection, blockId, { floors });
  return floors.length > 0 && floors.every(floor => isFloorChecked(blockFloors[floor.id]));
};

export const usesCollapsibleFloorTasks = categoryId =>
  categoryId === 'emp-and-other' || categoryId === 'house-keeping';

export const usesSimpleFloorCheckboxes = categoryId => categoryId === 'security';

/** Floor task state for one block; supports legacy flat `floors` on selection. */
export const getBlockFloors = (selection, blockId, { floors = [], tasks = [] } = {}) => {
  if (!blockId) return createEmptyFloorsState(floors, tasks);
  if (selection?.floorsByBlock?.[blockId]) {
    return selection.floorsByBlock[blockId];
  }
  if (selection?.floors) {
    return selection.floors;
  }
  return createEmptyFloorsState(floors, tasks);
};

export const getActiveBlockTab = (selection, selectedBlockIds) => {
  const active = selection?.activeBlockTab;
  if (active && selectedBlockIds.includes(active)) return active;
  return selectedBlockIds[0] || null;
};

export const getFloorsForBlock = (siteCatalog, blockId) => {
  const block = siteCatalog?.blocks?.find(b => b.id === blockId);
  return block?.floors || [];
};

export const syncFloorsByBlockForSelection = (selection, nextBlockIds, siteCatalog) => {
  const tasks = siteCatalog?.tasks || [];
  const floorsByBlock = { ...(selection?.floorsByBlock || {}) };

  if (selection?.floors && Object.keys(floorsByBlock).length === 0 && nextBlockIds.length > 0) {
    nextBlockIds.forEach(blockId => {
      floorsByBlock[blockId] = { ...selection.floors };
    });
  }

  nextBlockIds.forEach(blockId => {
    if (!floorsByBlock[blockId]) {
      const floors = getFloorsForBlock(siteCatalog, blockId);
      floorsByBlock[blockId] = createEmptyFloorsState(floors, tasks);
    }
  });

  let activeBlockTab = selection?.activeBlockTab ?? null;
  if (!nextBlockIds.includes(activeBlockTab)) {
    activeBlockTab = nextBlockIds[0] || null;
  }

  const prevBlockIds = getBlockIds(selection);
  const newlyAdded = nextBlockIds.find(id => !prevBlockIds.includes(id));
  if (newlyAdded && (!activeBlockTab || !nextBlockIds.includes(activeBlockTab))) {
    activeBlockTab = newlyAdded;
  }

  return { floorsByBlock, activeBlockTab };
};

export const createEmptyCleaningAreaSelection = () => ({
  blockIds: [],
  activeBlockTab: null,
  floorsByBlock: {},
  employeeCategoryTab: DEFAULT_EMPLOYEE_CATEGORY_TAB,
  selectedEmployeeIdsByCategory: createEmptySelectedEmployeesByCategory(),
  employeeDescriptionsByCategory: createEmptyEmployeeDescriptionsByCategory(),
});

export const isFloorFullySelected = (floorState, tasks = []) =>
  tasks.length > 0 && tasks.every(task => floorState?.tasks?.[task.id]);

export const areAllFloorsFullySelectedForBlock = (
  selection,
  blockId,
  floors = [],
  tasks = []
) => {
  const blockFloors = getBlockFloors(selection, blockId, { floors, tasks });
  return (
    floors.length > 0 &&
    floors.every(floor => isFloorFullySelected(blockFloors[floor.id], tasks))
  );
};

export const areAllFloorsFullySelected = (
  selection,
  siteCatalog,
  blockId = null
) => {
  const blockIds = getBlockIds(selection);
  const resolvedBlockId = blockId || getActiveBlockTab(selection, blockIds);
  if (!resolvedBlockId) return false;
  const floors = getFloorsForBlock(siteCatalog, resolvedBlockId);
  const tasks = siteCatalog?.tasks || [];
  return areAllFloorsFullySelectedForBlock(
    selection,
    resolvedBlockId,
    floors,
    tasks
  );
};

export const getBlockIds = selection => {
  if (Array.isArray(selection?.blockIds)) return selection.blockIds;
  if (selection?.blockId) return [selection.blockId];
  return [];
};

/** @deprecated Use buildCleaningShiftAreaSummary */
export const getSelectedAreasSummary = (selection, siteCatalog) => {
  const summary = buildCleaningShiftAreaSummary(selection, siteCatalog);
  if (!summary) return null;
  return {
    blockLabels: summary.blocks.map(b => b.label),
    floors: summary.blocks.flatMap(b => b.floors.map(f => f.label)),
  };
};

/**
 * Full area + staff summary for the shift summary card.
 * @returns {null | { blocks: object[], categories: object[], amenities: object[] }}
 */
export const buildCleaningShiftAreaSummary = (selection, siteCatalog, employeesById = {}) => {
  const blockIds = getBlockIds(selection);
  const catalogBlocks = siteCatalog?.blocks || [];
  const tasks = siteCatalog?.tasks || [];

  const blocks = blockIds.map(blockId => {
    const meta = catalogBlocks.find(b => b.id === blockId);
    const floors = getFloorsForBlock(siteCatalog, blockId);
    const floorsState = getBlockFloors(selection, blockId, { floors, tasks });

    const floorDetails = floors
      .map(floor => {
        const state = floorsState[floor.id] || {};
        const selectedTaskLabels = tasks.filter(t => state.tasks?.[t.id]).map(t => t.label);
        const hasSelection = isFloorChecked(state) || selectedTaskLabels.length > 0;
        if (!hasSelection) return null;

        return {
          label: floor.label,
          securitySelected: isFloorChecked(state),
          tasks: selectedTaskLabels,
          description: (state.description || '').trim(),
        };
      })
      .filter(Boolean);

    return {
      id: blockId,
      label: meta?.label || blockId,
      blockType: meta?.block_type || 'block',
      floors: floorDetails,
    };
  });

  const categories = EMPLOYEE_CATEGORY_TABS.map(tab => {
    const employeeIds = getSelectedEmployeeIds(selection, tab.id);
    const description = getEmployeeDescriptionByCategory(selection, tab.id).trim();
    const employeeNames = employeeIds.map(id => {
      const emp = employeesById[id];
      return (
        emp?.display_name ||
        emp?.user_name ||
        emp?.name ||
        emp?.employee_name ||
        id
      );
    });
    return {
      id: tab.id,
      tabLabel: tab.label,
      sectionLabel: EMPLOYEE_CATEGORY_SECTION_LABELS[tab.id],
      employeeCount: employeeIds.length,
      employeeNames,
      description,
    };
  }).filter(c => c.employeeCount > 0 || c.description);

  const amenities = (siteCatalog?.amenities || []).map(a => ({
    id: a.id,
    label: a.label,
    amenityType: a.amenity_type,
  }));

  if (blocks.length === 0 && categories.length === 0) {
    return null;
  }

  return { blocks, categories, amenities };
};
